import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    backupApi,
    RestoreMode,
    WikiBackupEntry,
    WikiBackupSummary,
    WikiRestoreOutcome,
} from '../utils/api';
import {
    AlertTriangle,
    Archive,
    ArrowLeft,
    ArrowRight,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    Download,
    RotateCcw,
    ShieldAlert,
    Trash2,
    X,
} from 'lucide-react';

/**
 * Wiki backups and restores, for wiki administrators.
 *
 * Its own route rather than a panel on /admin, because /admin is gated on
 * system administration while a backup only needs Admin of the wiki - the same
 * gate as Manage Contents, which is why this sits beside it under /wiki.
 *
 * Restoring is the most destructive thing in BIKI, so the screen is arranged
 * so that a plan is always seen before anything is written: the Apply control
 * does not exist until a dry run has been read back, and changing the mode
 * throws the plan away rather than letting a merge plan authorise a replace.
 */
const WikiBackupsPage: React.FC = () => {
    const [backups, setBackups] = useState<WikiBackupEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const [summary, setSummary] = useState<WikiBackupSummary | null>(null);

    // The name currently downloading, so only that row shows a pending state.
    const [downloadingName, setDownloadingName] = useState<string | null>(null);
    const [downloadError, setDownloadError] = useState<string | null>(null);

    // ---- delete ---------------------------------------------------------
    // Confirmation is per row and in place: the account keeps soft-deleted
    // blobs for a week, so this is recoverable and does not warrant the
    // typed-name gate that replace mode has. `confirmingDelete` holds at most
    // one name, so arming a second row disarms the first.
    const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
    const [deletingName, setDeletingName] = useState<string | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // ---- restore -------------------------------------------------------
    // `plan` is a dry run: the same shape a real restore returns, with
    // `applied` false. It is the only thing that enables the Apply control,
    // and it is discarded whenever the archive or the mode changes.
    const [restoreName, setRestoreName] = useState<string | null>(null);
    const [restoreMode, setRestoreMode] = useState<RestoreMode>('merge');
    const [plan, setPlan] = useState<WikiRestoreOutcome | null>(null);
    const [planning, setPlanning] = useState(false);
    const [applying, setApplying] = useState(false);
    const [restoreError, setRestoreError] = useState<string | null>(null);
    const [restoreProblems, setRestoreProblems] = useState<string[]>([]);
    const [applied, setApplied] = useState<WikiRestoreOutcome | null>(null);
    const [confirmText, setConfirmText] = useState('');
    const [openList, setOpenList] = useState<string | null>(null);

    const restorePanelRef = useRef<HTMLDivElement | null>(null);

    const fetchBackups = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        const result = await backupApi.list();
        if (result.error) {
            setLoadError(result.error);
            setBackups([]);
        } else {
            setBackups(result.data ?? []);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchBackups();
    }, [fetchBackups]);

    /**
     * Building the archive takes several seconds, so the button holds a pending
     * state throughout and the list is re-read afterwards - the new archive is
     * only in the listing once the server has written it.
     */
    const handleCreate = async () => {
        setCreating(true);
        setCreateError(null);
        setSummary(null);

        const result = await backupApi.create();
        if (result.error || !result.data) {
            setCreateError(result.error || 'Backup failed.');
        } else {
            setSummary(result.data);
            await fetchBackups();
        }
        setCreating(false);
    };

    /**
     * The download endpoint needs the bearer token, so this cannot be a plain
     * `<a download href>` - that would arrive unauthenticated and 401. The
     * archive is fetched as a blob and saved from an object URL instead.
     */
    const handleDownload = async (name: string) => {
        setDownloadingName(name);
        setDownloadError(null);

        const result = await backupApi.download(name);
        if (result.error || !result.data) {
            setDownloadError(result.error || `Could not download ${name}.`);
            setDownloadingName(null);
            return;
        }

        const objectUrl = URL.createObjectURL(result.data);
        try {
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = name;
            document.body.appendChild(link);
            link.click();
            link.remove();
        } finally {
            // Revoking immediately is safe: the browser has already taken the
            // blob for the save it just started.
            URL.revokeObjectURL(objectUrl);
        }
        setDownloadingName(null);
    };

    /**
     * Removes one archive.
     *
     * If the archive being removed is the one the restore panel is showing, the
     * panel goes with it: its plan describes an archive that no longer exists,
     * and leaving it up would offer an Apply that could only fail.
     */
    const handleDelete = async (name: string) => {
        setDeletingName(name);
        setDeleteError(null);

        const result = await backupApi.remove(name);
        if (result.error) {
            setDeleteError(result.error);
            setDeletingName(null);
            return;
        }

        if (restoreName === name) {
            closeRestore();
        }
        if (summary?.name === name) {
            setSummary(null);
        }
        setConfirmingDelete(null);
        setDeletingName(null);
        await fetchBackups();
    };

    /** Everything about a plan, cleared whenever what it describes changes. */
    const discardPlan = () => {
        setPlan(null);
        setApplied(null);
        setRestoreError(null);
        setRestoreProblems([]);
        setConfirmText('');
        setOpenList(null);
    };

    const openRestore = (name: string) => {
        setRestoreName(name);
        setRestoreMode('merge');
        discardPlan();
        // The panel sits above the table, so a row further down would otherwise
        // open it off-screen.
        window.requestAnimationFrame(() => {
            restorePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    };

    const closeRestore = () => {
        setRestoreName(null);
        discardPlan();
    };

    /**
     * A plan describes one mode. Keeping it on screen after the mode changed
     * would let someone read a merge plan - which deletes nothing - and then
     * apply a replace, which deletes whatever the archive does not contain.
     */
    const chooseMode = (mode: RestoreMode) => {
        if (mode === restoreMode) return;
        setRestoreMode(mode);
        discardPlan();
    };

    const handlePreview = async () => {
        if (!restoreName) return;
        setPlanning(true);
        discardPlan();

        const result = await backupApi.restore(restoreName, { mode: restoreMode, apply: false });
        if (result.error || !result.data) {
            setRestoreError(result.error || 'Could not read the archive.');
            setRestoreProblems(result.problems ?? []);
        } else {
            setPlan(result.data);
        }
        setPlanning(false);
    };

    /**
     * The API requires `confirm` to equal the backup's own name. In replace
     * mode the administrator types it; in merge mode the reviewed plan is the
     * deliberate act, so the page sends it. Merge never deletes - it can only
     * create and overwrite - and the safety backup makes it reversible.
     */
    const handleApply = async () => {
        if (!restoreName || !plan) return;
        setApplying(true);
        setRestoreError(null);
        setRestoreProblems([]);

        const result = await backupApi.restore(restoreName, {
            mode: restoreMode,
            apply: true,
            confirm: restoreName,
        });

        if (result.error || !result.data) {
            setRestoreError(result.error || 'Restore failed.');
            setRestoreProblems(result.problems ?? []);
        } else {
            setApplied(result.data);
            setPlan(null);
            setConfirmText('');
            // A real restore takes a safety backup first, so the listing has
            // gained an archive that is not on screen yet.
            await fetchBackups();
        }
        setApplying(false);
    };

    const formatSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
        return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    };

    /** Local time, with the exact instant on hover - archives are named in UTC. */
    const formatWhen = (iso: string): string => {
        const parsed = new Date(iso);
        if (Number.isNaN(parsed.getTime())) return iso;
        return parsed.toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    /** One figure of what the archive holds. */
    const renderCount = (label: string, value: number) => (
        <div>
            <p className="text-2xl font-semibold text-gray-800">{value}</p>
            <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
        </div>
    );

    /**
     * A collapsed list of labels. These run to hundreds of lines on a first
     * restore, so they open one at a time and only on request - but they are
     * the substance of the plan, so nothing is truncated once opened.
     */
    const renderList = (key: string, title: string, items: string[], tone: 'plain' | 'danger' = 'plain') => {
        if (items.length === 0) return null;
        const open = openList === key;
        return (
            <div className="border-t border-gray-200 first:border-t-0">
                <button
                    onClick={() => setOpenList(open ? null : key)}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors"
                >
                    {open ? (
                        <ChevronDown className="w-4 h-4 shrink-0 text-gray-400" />
                    ) : (
                        <ChevronRight className="w-4 h-4 shrink-0 text-gray-400" />
                    )}
                    <span className={tone === 'danger' ? 'text-red-700' : 'text-gray-700'}>{title}</span>
                    <span className="ml-auto text-xs text-gray-400 tabular-nums">{items.length}</span>
                </button>
                {open && (
                    <ul className="px-4 pb-3 pl-10 space-y-1 max-h-64 overflow-y-auto">
                        {items.map((item, index) => (
                            <li
                                key={`${key}-${index}`}
                                className={`text-sm break-words ${tone === 'danger' ? 'text-red-700' : 'text-gray-600'}`}
                            >
                                {item}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
    };

    /** "+3 new · 2 updated · 29 unchanged" for one kind of content. */
    const renderChangeLine = (label: string, set: WikiRestoreOutcome['sections']) => (
        <div className="flex items-baseline gap-2">
            <span className="text-sm text-gray-500 w-24 shrink-0">{label}</span>
            <span className="text-sm text-gray-800 tabular-nums">
                <span className="font-semibold">{set.create}</span> new
                <span className="mx-1.5 text-gray-300">&middot;</span>
                <span className="font-semibold">{set.update}</span> updated
                <span className="mx-1.5 text-gray-300">&middot;</span>
                <span className="text-gray-500">{set.unchanged} unchanged</span>
            </span>
        </div>
    );

    /**
     * The plan, or what a completed restore turned out to do. Both come back in
     * the same shape, so both render through here - `outcome.applied` is the
     * only thing that separates "would" from "did".
     */
    const renderOutcome = (outcome: WikiRestoreOutcome) => {
        const deletes =
            outcome.deletions.sections.length +
            outcome.deletions.articles.length;
        const crossEnvironment =
            !!outcome.sourceEnvironment &&
            outcome.sourceEnvironment.toLowerCase() !== outcome.targetEnvironment.toLowerCase();

        return (
            <div className="mt-5 border border-gray-200 rounded-lg overflow-hidden">
                {/* Where the content is coming from and going to. This is the
                    point of the whole feature - comparing one environment
                    against another - so it leads. */}
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-sm font-medium text-gray-700">
                        {outcome.sourceEnvironment || 'unknown source'}
                    </span>
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">{outcome.targetEnvironment}</span>
                    <span className="text-xs text-gray-500 uppercase tracking-wider ml-auto">
                        {outcome.mode}
                    </span>
                </div>

                {/* Archives from every environment sit in one container, so the
                    list this was chosen from mixes them. Copying one environment
                    over another is the reason the feature exists, but it is also
                    the way to overwrite production with a development wiki by
                    accident, so it is stated rather than left to be inferred from
                    the archive's name. */}
                {crossEnvironment && (
                    <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border-b border-amber-200">
                        <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" />
                        <p className="text-sm text-amber-700">
                            This archive was taken in <strong>{outcome.sourceEnvironment}</strong> and
                            is about to be written into <strong>{outcome.targetEnvironment}</strong>.
                            Sections and articles are matched by name and title rather than by id, so
                            the two environments do not have to share any history — and content here
                            will be overwritten with the other environment's version of it.
                        </p>
                    </div>
                )}

                <div className="px-4 py-4 space-y-2">
                    {renderChangeLine('Sections', outcome.sections)}
                    {renderChangeLine('Articles', outcome.articles)}
                    <div className="flex items-baseline gap-2">
                        <span className="text-sm text-gray-500 w-24 shrink-0">Images</span>
                        <span className="text-sm text-gray-800 tabular-nums">
                            <span className="font-semibold">{outcome.images.upload}</span> to upload
                            <span className="mx-1.5 text-gray-300">&middot;</span>
                            <span className="text-gray-500">
                                {outcome.images.reuseSameName + outcome.images.reuseByOriginalName} already here
                            </span>
                        </span>
                    </div>
                    <div className="flex items-baseline gap-2">
                        <span className="text-sm text-gray-500 w-24 shrink-0">Note links</span>
                        <span className="text-sm text-gray-800 tabular-nums">
                            <span className="font-semibold">{outcome.releaseNoteLinks.notes}</span> release
                            {outcome.releaseNoteLinks.notes === 1 ? ' note' : ' notes'}
                            <span className="mx-1.5 text-gray-300">&middot;</span>
                            <span className="text-gray-500">
                                {outcome.releaseNoteLinks.referencesMapped} references
                            </span>
                        </span>
                    </div>
                </div>

                {/* What will actually change, by name. */}
                <div className="border-t border-gray-200">
                    {renderList('sections-create', 'Sections to create', outcome.sections.toCreate)}
                    {renderList('sections-update', 'Sections to update', outcome.sections.toUpdate)}
                    {renderList('articles-create', 'Articles to create', outcome.articles.toCreate)}
                    {renderList('articles-update', 'Articles to update', outcome.articles.toUpdate)}
                    {renderList('images-upload', 'Images to upload', outcome.images.toUpload)}
                    {renderList('note-links', 'Release-note references', outcome.releaseNoteLinks.detail)}
                </div>

                {/* Deletions are the whole difference between the two modes, so
                    they get their own block rather than a line in the summary. */}
                {deletes > 0 && (
                    <div className="border-t border-red-200 bg-red-50">
                        <div className="flex items-start gap-3 px-4 py-3">
                            <Trash2 className="w-5 h-5 shrink-0 text-red-600" />
                            <div>
                                <h4 className="text-sm font-semibold text-red-800">
                                    {outcome.applied ? 'Deleted' : 'Will be deleted'}: {deletes} item
                                    {deletes === 1 ? '' : 's'}
                                </h4>
                                <p className="text-sm text-red-700 mt-0.5">
                                    Content this environment has and the archive does not.
                                </p>
                            </div>
                        </div>
                        {renderList('delete-sections', 'Sections', outcome.deletions.sections, 'danger')}
                        {renderList('delete-articles', 'Articles', outcome.deletions.articles, 'danger')}
                        {renderList('delete-blocked', 'Could not be deleted', outcome.deletions.blocked, 'danger')}
                        {renderList(
                            'delete-note-refs',
                            'Release notes losing a reference',
                            outcome.deletions.noteReferencesCleared,
                            'danger',
                        )}
                    </div>
                )}

                {/* Broken image references are inherited from the archive, not
                    caused by the restore - worth saying, not worth blocking on. */}
                {outcome.images.brokenReferences.length > 0 && (
                    <div className="border-t border-gray-200 px-4 py-3">
                        <p className="text-sm text-gray-500">
                            {outcome.images.brokenReferences.length === 1
                                ? '1 image reference in the archive points'
                                : `${outcome.images.brokenReferences.length} image references in the archive point`}{' '}
                            at files it does not contain, and will stay broken.
                        </p>
                    </div>
                )}

                {outcome.warnings.length > 0 && (
                    <div className="border-t border-amber-200 bg-amber-50 px-4 py-3">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" />
                            <ul className="text-sm text-amber-700 space-y-1">
                                {outcome.warnings.map((warning, index) => (
                                    <li key={index}>{warning}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const modeOption = (mode: RestoreMode, title: string, description: string) => {
        const selected = restoreMode === mode;
        return (
            <button
                key={mode}
                onClick={() => chooseMode(mode)}
                aria-pressed={selected}
                className={`flex-1 text-left p-3 rounded-lg border transition-colors ${
                    selected
                        ? mode === 'replace'
                            ? 'border-red-500 bg-red-50'
                            : 'border-brand-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                }`}
            >
                <span
                    className={`block text-sm font-medium ${
                        selected && mode === 'replace' ? 'text-red-800' : 'text-gray-800'
                    }`}
                >
                    {title}
                </span>
                <span
                    className={`block text-xs mt-0.5 ${
                        selected && mode === 'replace' ? 'text-red-700' : 'text-gray-500'
                    }`}
                >
                    {description}
                </span>
            </button>
        );
    };

    const confirmed = restoreMode === 'merge' || confirmText.trim() === restoreName;

    return (
        <div className="bx-wrap py-4 fade-in">
            {/* Header */}
            <div className="flex items-start justify-between mb-8 gap-4">
                <div>
                    <Link
                        to="/wiki"
                        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-2"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Documentation
                    </Link>
                    <h1 className="text-3xl font-bold text-gray-800">Backups</h1>
                    <p className="text-gray-500 mt-1">
                        Export every section, article and image, along with the release-note
                        references to documentation, as a single zip archive — and restore one back
                        into this environment.
                    </p>
                </div>
                <button
                    onClick={handleCreate}
                    disabled={creating}
                    className="shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    <Archive className="w-4 h-4" />
                    {creating ? 'Backing up…' : 'Back up now'}
                </button>
            </div>

            {creating && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-brand-700 text-sm">
                    Building the archive. The whole wiki is being zipped, images included, so
                    this takes a few seconds.
                </div>
            )}

            {/* Each failure is its own banner: a download that 401s and a
                backup that fell over are different problems, and collapsing
                them into one line hides whichever came second. */}
            {[loadError, createError, downloadError, deleteError]
                .filter((message): message is string => !!message)
                .map((message) => (
                    <div
                        key={message}
                        className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
                    >
                        {message}
                    </div>
                ))}

            {/* What the backup just taken contained. */}
            {summary && (
                <div className="mb-8 bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-800">Backup complete</h3>
                        <p className="text-sm text-gray-500 mt-0.5">
                            <span className="font-mono">{summary.name}</span>
                            <span className="mx-1.5 text-gray-300">&middot;</span>
                            {formatSize(summary.sizeBytes)}
                        </p>
                    </div>
                    <div className="px-6 py-5 grid grid-cols-2 gap-5 sm:grid-cols-4">
                        {renderCount('Sections', summary.sections)}
                        {renderCount('Articles', summary.articles)}
                        {renderCount('Images', summary.images)}
                        {renderCount('Note links', summary.releaseNoteLinks)}
                    </div>

                    {/*
                        These two counts are the reason the API returns them: a
                        non-zero value means the archive is missing something the
                        wiki still points at, which is worth knowing now rather
                        than at restore time.
                    */}
                    {(summary.missingImages > 0 || summary.droppedArticleRefs > 0) && (
                        <div className="mx-6 mb-5 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" />
                                <div>
                                    <h4 className="text-sm font-semibold text-amber-800">
                                        Not everything could be captured
                                    </h4>
                                    <ul className="mt-1.5 text-sm text-amber-700 space-y-1">
                                        {summary.missingImages > 0 && (
                                            <li>
                                                {summary.missingImages} referenced image
                                                {summary.missingImages === 1 ? '' : 's'} could not be
                                                included: articles point at images that are no longer
                                                in storage.
                                            </li>
                                        )}
                                        {summary.droppedArticleRefs > 0 && (
                                            <li>
                                                {summary.droppedArticleRefs} release-note reference
                                                {summary.droppedArticleRefs === 1 ? '' : 's'} had to be
                                                dropped: the articles referenced are outside this
                                                backup, so those links cannot be restored from it.
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Restore, for whichever archive was chosen from the table. */}
            {restoreName && (
                <div
                    ref={restorePanelRef}
                    className="mb-8 bg-white rounded-xl border border-gray-200 overflow-hidden"
                >
                    <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800">Restore</h3>
                            <p className="text-sm text-gray-500 mt-0.5 font-mono break-all">
                                {restoreName}
                            </p>
                        </div>
                        <button
                            onClick={closeRestore}
                            aria-label="Close restore"
                            className="shrink-0 p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="px-6 py-5">
                        {/* Mode. Replace is offered everywhere and refused by the
                            API outside development - the environment gate stays
                            in one place rather than being second-guessed here. */}
                        <div className="flex flex-col sm:flex-row gap-3">
                            {modeOption(
                                'merge',
                                'Merge',
                                'Adds and overwrites what the archive holds. Deletes nothing.',
                            )}
                            {modeOption(
                                'replace',
                                'Replace',
                                'Also deletes content the archive does not contain. Development only.',
                            )}
                        </div>

                        {!plan && !applied && (
                            <button
                                onClick={handlePreview}
                                disabled={planning}
                                className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                <RotateCcw className="w-4 h-4" />
                                {planning ? 'Reading the archive…' : 'Preview changes'}
                            </button>
                        )}

                        {restoreError && (
                            <div className="mt-5 p-4 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm text-red-700">{restoreError}</p>
                                {restoreProblems.length > 0 && (
                                    <ul className="mt-2 text-sm text-red-700 list-disc pl-5 space-y-1 max-h-48 overflow-y-auto">
                                        {restoreProblems.map((problem, index) => (
                                            <li key={index}>{problem}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}

                        {/* The plan, and the control that applies it. */}
                        {plan && (
                            <>
                                {renderOutcome(plan)}

                                <div className="mt-5 pt-5 border-t border-gray-200">
                                    {restoreMode === 'replace' && (
                                        <div className="mb-4">
                                            <div className="flex items-start gap-3 mb-3">
                                                <ShieldAlert className="w-5 h-5 shrink-0 text-red-600" />
                                                <p className="text-sm text-red-700">
                                                    Replace deletes content this environment has and the
                                                    archive does not. Type the archive name to confirm.
                                                </p>
                                            </div>
                                            <input
                                                type="text"
                                                value={confirmText}
                                                onChange={(event) => setConfirmText(event.target.value)}
                                                placeholder={restoreName}
                                                spellCheck={false}
                                                autoComplete="off"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                                            />
                                        </div>
                                    )}

                                    <div className="flex flex-wrap items-center gap-3">
                                        <button
                                            onClick={handleApply}
                                            disabled={applying || !confirmed}
                                            className={`inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed ${
                                                restoreMode === 'replace'
                                                    ? 'bg-red-600 hover:bg-red-700'
                                                    : 'bg-brand-600 hover:bg-brand-700'
                                            }`}
                                        >
                                            <RotateCcw className="w-4 h-4" />
                                            {applying
                                                ? 'Restoring…'
                                                : restoreMode === 'replace'
                                                  ? 'Replace the wiki with this archive'
                                                  : 'Apply this restore'}
                                        </button>
                                        <button
                                            onClick={discardPlan}
                                            disabled={applying}
                                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            Discard plan
                                        </button>
                                        <p className="text-xs text-gray-400">
                                            A backup of the current state is taken first, so this can be
                                            undone.
                                        </p>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Done. The safety backup is the undo path, so it is the
                            first thing said rather than a footnote. */}
                        {applied && (
                            <>
                                <div className="mt-5 p-4 bg-green-50 border border-green-200 rounded-lg">
                                    <div className="flex items-start gap-3">
                                        <CheckCircle2 className="w-5 h-5 shrink-0 text-green-600" />
                                        <div>
                                            <h4 className="text-sm font-semibold text-green-800">
                                                Restore complete
                                            </h4>
                                            {applied.safetyBackup && (
                                                <p className="text-sm text-green-700 mt-1">
                                                    The state before this restore was saved as{' '}
                                                    <span className="font-mono break-all">
                                                        {applied.safetyBackup}
                                                    </span>
                                                    . Restoring that archive undoes it.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {renderOutcome(applied)}
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Stored archives, newest first as the API returns them. */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
                    <h3 className="text-lg font-semibold text-gray-800">Stored backups</h3>
                    <button
                        onClick={fetchBackups}
                        disabled={loading}
                        className="text-sm text-brand-600 hover:text-brand-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Loading…' : `Refresh (${backups.length})`}
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-8">
                        <div className="spinner"></div>
                    </div>
                ) : backups.length === 0 ? (
                    <p className="text-sm text-gray-400 italic px-6 py-8 text-center">
                        No backups stored yet. Take one with “Back up now”.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Archive
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Created
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Size
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {backups.map((backup) => (
                                    <tr
                                        key={backup.name}
                                        className={`transition-colors ${
                                            restoreName === backup.name ? 'bg-blue-50' : 'hover:bg-gray-50'
                                        }`}
                                    >
                                        <td className="px-6 py-4">
                                            <p className="text-sm font-medium text-gray-800 font-mono break-all">
                                                {backup.name}
                                            </p>
                                            {backup.sourceEnvironment && (
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    taken in {backup.sourceEnvironment}
                                                </p>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap" title={backup.created}>
                                            {formatWhen(backup.created)}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                                            {formatSize(backup.sizeBytes)}
                                        </td>
                                        <td className="px-6 py-4">
                                            {/* The row's own actions are replaced by the
                                                confirmation rather than joined by it, so the
                                                Delete that armed it cannot be clicked twice
                                                and the only choices are Delete or Cancel. */}
                                            {confirmingDelete === backup.name ? (
                                                <div className="flex items-center justify-end gap-2">
                                                    <span className="text-sm text-red-700">
                                                        Delete this archive?
                                                    </span>
                                                    <button
                                                        onClick={() => handleDelete(backup.name)}
                                                        disabled={deletingName !== null}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                        {deletingName === backup.name ? 'Deleting…' : 'Delete'}
                                                    </button>
                                                    <button
                                                        onClick={() => setConfirmingDelete(null)}
                                                        disabled={deletingName !== null}
                                                        className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => handleDownload(backup.name)}
                                                        disabled={downloadingName !== null}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <Download className="w-3.5 h-3.5" />
                                                        {downloadingName === backup.name ? 'Downloading…' : 'Download'}
                                                    </button>
                                                    <button
                                                        onClick={() => openRestore(backup.name)}
                                                        disabled={applying || planning}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <RotateCcw className="w-3.5 h-3.5" />
                                                        Restore
                                                    </button>
                                                    {/* Quieter than its neighbours until hovered:
                                                        it is the one action here that removes a
                                                        way back.

                                                        Disabled rather than hidden when this
                                                        environment may not delete the archive: the
                                                        control is missing for a reason, and a gap
                                                        where it should be does not say what that
                                                        reason is. */}
                                                    <button
                                                        onClick={() => setConfirmingDelete(backup.name)}
                                                        disabled={deletingName !== null || applying || !backup.canDelete}
                                                        aria-label={'Delete ' + backup.name}
                                                        title={
                                                            backup.canDelete
                                                                ? undefined
                                                                : `Only ${backup.sourceEnvironment || 'the environment that took it'}`
                                                                  + ' can delete this archive. This environment deletes only its own.'
                                                        }
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-500 rounded-lg hover:bg-red-50 hover:text-red-700 hover:border-red-200 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500 disabled:hover:border-gray-300"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                        Delete
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            <p className="text-xs text-gray-400 mt-3">
                An archive holds every article body, so it is as sensitive as the wiki itself.
                Downloads, restores and deletes are authorised the same way as taking a backup.
                A deleted archive is retained by Azure for seven days, and archives are pruned
                automatically after thirty. Every environment's archives are listed here so that
                one can be restored into another; deleting is narrower — production can remove any
                archive, and every other environment only the ones it took.
            </p>
        </div>
    );
};

export default WikiBackupsPage;
